/**
 * @file Source management API service.
 */

import { ApiService } from './apiService';
import { API_ENDPOINTS } from '../constants/api';

export type SourceStatus = 'active' | 'inactive' | string;

export interface Source {
  id: string;
  name: string;
  status: SourceStatus;
}

export interface SourceFormData {
  name: string;
}

export interface SourcesPagination {
  page: number;
  per_page: number;
  total: number;
  total_page: number;
}

export interface SourcesListResult {
  sources: Source[];
  pagination: SourcesPagination;
}

function parseSourceId(value: unknown): string {
  if (value == null || value === '') return '';
  return String(value);
}

/** Maps API source object: { id, name, status, deleted_at, ... } */
function normalizeSourceRow(row: unknown): Source | null {
  const r = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
  const id = parseSourceId(r['id']);
  if (!id) return null;
  if (r['deleted_at'] != null && r['deleted_at'] !== '') return null;

  return {
    id,
    name: r['name'] == null ? '' : String(r['name']),
    status: String(r['status'] ?? 'active').toLowerCase(),
  };
}

function extractSourceRows(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  if (!raw || typeof raw !== 'object') return [];
  const root = raw as Record<string, unknown>;
  const data = root['data'];
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const inner = data as Record<string, unknown>;
    if (Array.isArray(inner['sources'])) return inner['sources'];
    if (Array.isArray(inner['data'])) return inner['data'];
  }
  if (Array.isArray(root['sources'])) return root['sources'];
  return [];
}

function normalizeSourcesList(raw: unknown): Source[] {
  return extractSourceRows(raw)
    .map(normalizeSourceRow)
    .filter((s): s is Source => s !== null);
}

function normalizeSourcesListResponse(
  raw: unknown,
  requestedPage: number,
  fallbackPerPage: number,
): SourcesListResult {
  const root = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const pagSource =
    root['pagination'] ??
    (root['data'] && typeof root['data'] === 'object' && !Array.isArray(root['data'])
      ? (root['data'] as Record<string, unknown>)['pagination']
      : undefined);

  const pag =
    pagSource && typeof pagSource === 'object'
      ? (pagSource as Record<string, unknown>)
      : {};

  const per_page =
    Number(pag['per_page'] ?? fallbackPerPage) || fallbackPerPage;
  const total = Number(pag['total'] ?? 0) || 0;
  const allSources = normalizeSourcesList(raw);
  const resolvedTotal = total > 0 ? total : allSources.length;
  const total_page = Math.max(1, Math.ceil(resolvedTotal / per_page) || 1);
  const page = Math.min(Math.max(1, requestedPage), total_page);

  // API sometimes returns all rows in one response; slice for the requested page.
  const pageSources =
    allSources.length > per_page
      ? allSources.slice((page - 1) * per_page, page * per_page)
      : allSources;

  return {
    sources: pageSources,
    pagination: {
      page,
      per_page,
      total: resolvedTotal,
      total_page,
    },
  };
}

/** Single-item envelope: { success, message, data: { id, name, status, ... } } */
function normalizeSourceResponse(raw: unknown): Source {
  if (raw && typeof raw === 'object') {
    const root = raw as Record<string, unknown>;
    const fromData = normalizeSourceRow(root['data']);
    if (fromData) return fromData;
    const direct = normalizeSourceRow(root);
    if (direct) return direct;
  }
  throw new Error('Invalid source response from server');
}

export function isSourceActive(status: SourceStatus): boolean {
  return status === 'active' || status === '1';
}

export class SourcesService {
  /** GET sources?page=&per_page= — Source Management table (active + inactive). */
  static async listSources(
    page: number = 1,
    perPage: number = 10,
  ): Promise<SourcesListResult> {
    try {
      const query = new URLSearchParams({
        page: String(page),
        per_page: String(perPage),
      });
      const response = await ApiService.makeRequest<Record<string, unknown>>(
        `${API_ENDPOINTS.SOURCES}?${query.toString()}`,
        'GET',
      );
      return normalizeSourcesListResponse(response, page, perPage);
    } catch (error) {
      throw error instanceof Error ? error : new Error('Failed to fetch sources');
    }
  }

  static async createSource(data: SourceFormData): Promise<Source> {
    try {
      const body = new FormData();
      body.append('name', data.name.trim());

      const response = await ApiService.makeRequest<Record<string, unknown>>(
        API_ENDPOINTS.SOURCES,
        'POST',
        body,
      );
      return normalizeSourceResponse(response);
    } catch (error) {
      throw error instanceof Error ? error : new Error('Failed to create source');
    }
  }

  static async updateSource(id: string, data: SourceFormData): Promise<Source> {
    try {
      const body = new FormData();
      body.append('name', data.name.trim());

      const response = await ApiService.makeRequest<Record<string, unknown>>(
        API_ENDPOINTS.sourceById(id),
        'POST',
        body,
      );
      return normalizeSourceResponse(response);
    } catch (error) {
      throw error instanceof Error ? error : new Error('Failed to update source');
    }
  }

  /** POST sources/{id}/status/active | sources/{id}/status/inactive */
  static async toggleSourceStatus(id: string, status: 'active' | 'inactive'): Promise<Source> {
    try {
      const response = await ApiService.makeRequest<Record<string, unknown>>(
        API_ENDPOINTS.sourceStatusPath(id, status),
        'POST',
      );
      return normalizeSourceResponse(response);
    } catch (error) {
      throw error instanceof Error ? error : new Error('Failed to update source status');
    }
  }

  static async deleteSource(id: string): Promise<void> {
    try {
      await ApiService.makeRequest<{ message?: string }>(
        API_ENDPOINTS.sourceById(id),
        'DELETE',
        new URLSearchParams(),
      );
    } catch (error) {
      throw error instanceof Error ? error : new Error('Failed to delete source');
    }
  }

  static validateSourceForm(data: SourceFormData): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    const name = data.name.trim();
    if (!name) {
      errors.push('Source name is required');
    } else if (name.length < 2) {
      errors.push('Source name must be at least 2 characters');
    } else if (name.length > 100) {
      errors.push('Source name must be at most 100 characters');
    }
    return { isValid: errors.length === 0, errors };
  }
}
